import {math} from "@game/coregame/helpers";
import {Vector2} from "@game/coregame/interfaces/Vector2";
import {Segment} from "./GroupModel";

class Polygon {
    public Points: Vector2[];
    constructor(segment: Segment) {
        this.Points = BotUtility.CalculateBounds(
            segment.Center,
            segment.Width,
            segment.Height,
            segment.Angle
        );
    }
}

export class BotUtility {
    static IsSegmentIntersectCircle(circleCenter: Vector2, radius: float, segment: Segment): bool {
        return BotUtility.IsRectIntersectCircle(
            circleCenter,
            radius,
            segment.Center,
            segment.Width,
            segment.Height,
            segment.Angle
        );
    }

    static IsRectIntersectCircle(
        circleCenter: Vector2,
        circleRadius: float,
        rectangleCenter: Vector2,
        width: float,
        height: float,
        rotation: float
    ): bool {
        const bounds = BotUtility.CalculateBounds(rectangleCenter, width, height, rotation);

        return (
            BotUtility.IsPointInRectangle(circleCenter, bounds) ||
            BotUtility.IsLineIntersectCircle(circleCenter, circleRadius, bounds[0], bounds[1]) ||
            BotUtility.IsLineIntersectCircle(circleCenter, circleRadius, bounds[1], bounds[2]) ||
            BotUtility.IsLineIntersectCircle(circleCenter, circleRadius, bounds[2], bounds[3]) ||
            BotUtility.IsLineIntersectCircle(circleCenter, circleRadius, bounds[3], bounds[0])
        );
    }

    static CalculateBounds(
        rectangleCenter: Vector2,
        width: float,
        height: float,
        rotation: float
    ): Vector2[] {
        const bounds: Vector2[] = [];

        const halfWidth = width * 0.5;
        const halfHeight = height * 0.5;
        const angle = rotation * math.DEG2RAD;

        bounds[0] = math.rotateAround(rectangleCenter, [-halfWidth, halfHeight], angle);
        bounds[1] = math.rotateAround(rectangleCenter, [halfWidth, halfHeight], angle);
        bounds[2] = math.rotateAround(rectangleCenter, [halfWidth, -halfHeight], angle);
        bounds[3] = math.rotateAround(rectangleCenter, [-halfWidth, -halfHeight], angle);

        return bounds;
    }

    static DrawBox(bounds: Vector2[]): void {
        // Debug.DrawLine(bounds[0], bounds[1], Color.red, 1);
        // Debug.DrawLine(bounds[1], bounds[2], Color.red, 1);
        // Debug.DrawLine(bounds[2], bounds[3], Color.red, 1);
        // Debug.DrawLine(bounds[3], bounds[0], Color.red, 1);
    }

    static IsPointInRectangle(circleCenter: Vector2, bounds: Vector2[]): bool {
        const ap = math.sub(bounds[0], circleCenter);
        const ab = math.sub(bounds[0], bounds[1]);
        const ad = math.sub(bounds[0], bounds[3]);
        const isPointInsideRect =
            0 <= math.dot(ap, ab) &&
            math.dot(ap, ab) <= math.dot(ab, ab) &&
            0 <= math.dot(ap, ad) &&
            math.dot(ap, ad) <= math.dot(ad, ad);

        return isPointInsideRect;
    }

    static IsLineIntersectCircle(center: Vector2, radius: float, p1: Vector2, p2: Vector2): bool {
        const isCorner1InCircle = math.dist(center, p1) <= radius;
        const isCorner2InCircle = math.dist(center, p2) <= radius;

        const [x1, y1] = p1;
        const [x2, y2] = p2;
        const [x3, y3] = center;

        const px = x2 - x1;
        const py = y2 - y1;
        const dAB = px * px + py * py;

        const u = ((x3 - x1) * px + (y3 - y1) * py) / dAB;
        const x = x1 + u * px;
        const y = y1 + u * py;

        const d: Vector2 = [x, y];

        const isPointBetween = math.approximately(
            math.dist(p1, d) + math.dist(p2, d),
            math.dist(p1, p2)
        );
        const isInRadius = math.dist(center, d) <= radius;

        return (isPointBetween && isInRadius) || isCorner1InCircle || isCorner2InCircle;
    }

    public static IsSegmentIntersectSegment(segment: Segment, otherSegment: Segment): bool {
        return BotUtility.IsRectanglesIntersecting(new Polygon(segment), new Polygon(otherSegment));
    }

    static IsRectanglesIntersecting(a: Polygon, b: Polygon): bool {
        const poligons = [a, b];
        for (var polygon of poligons) {
            for (let i1 = 0; i1 < polygon.Points.length; i1++) {
                let i2 = (i1 + 1) % polygon.Points.length;
                const [p1x, p1y] = polygon.Points[i1];
                const [p2x, p2y] = polygon.Points[i2];

                var normal: Vector2 = [p2y - p1y, p1x - p2x];

                let minA: number = NaN,
                    maxA: number = NaN;
                for (const p of a.Points) {
                    const [px, py] = p;
                    const [nx, ny] = normal;
                    const projected = nx * px + ny * py;
                    if (isNaN(minA) || projected < minA) minA = projected;
                    if (isNaN(maxA) || projected > maxA) maxA = projected;
                }

                let minB: number = NaN,
                    maxB: number = NaN;
                for (const p of b.Points) {
                    const [px, py] = p;
                    const [nx, ny] = normal;
                    const projected = nx * px + ny * py;
                    if (isNaN(minB) || projected < minB) minB = projected;
                    if (isNaN(maxB) || projected > maxB) maxB = projected;
                }

                if (maxA < minB || maxB < minA) return false;
            }
        }

        return true;
    }
}
